import type { ConsentSignals } from '@/lib/consent/consent-signals';

import type { AnalyticsAdapter, CaptureOptions } from './types';

import { stripPiiKeys } from './strip-pii';

/**
 * Type alias for the lazy-imported `@segment/analytics-next` module's
 * AnalyticsBrowser. Pulled in as a type only so TS knows the shape
 * without forcing the actual bundle into the eagerly-loaded chunk.
 *
 * `AnalyticsBrowser` is a class — in type position this resolves to its
 * instance type, which is what we hold via `AnalyticsBrowser.load(...)`
 * (the static factory returns an instance, not the constructor).
 *
 * Do NOT add `typeof` here. That would resolve to the constructor type
 * (just static members like `.load`), and the holder assignment +
 * downstream `sdk.browser.track(...)` calls would type-error because
 * `track` / `identify` / `reset` are instance methods, not static.
 */
type SegmentAnalytics = import('@segment/analytics-next').AnalyticsBrowser;

/**
 * `AnalyticsBrowser` extends `Promise<[Analytics, Context]>`, so storing
 * one directly as the resolved value of an async function causes TS to
 * Promise-flatten the return type into the tuple. Wrapping in a holder
 * preserves the buffered-facade reference (which is what we actually
 * want to call `.track()` / `.identify()` on).
 */
interface SegmentSdkHolder {
  browser: SegmentAnalytics;
}

interface SegmentAdapterConfig {
  writeKey: string;
}

/**
 * Module-scoped promise so multiple adapter instances on the same page
 * cannot trigger duplicate `AnalyticsBrowser.load` calls. The first
 * adapter to call wins; any subsequent adapter shares the same instance
 * and just awaits this promise. In practice the
 * `getOrCreateAnalyticsClient` cache means only one adapter is
 * constructed per page — this is belt-and-braces against future
 * bypasses (tests, dev hot-reload, future call sites).
 */
let segmentLoadPromise: Promise<SegmentSdkHolder> | null = null;

/**
 * Module-scoped middleware install guard. The consent middleware must
 * fire exactly once per loaded SDK regardless of how many adapter
 * instances trigger the load — otherwise multi-instance paths would
 * stack middlewares, each closing over its own instance's stale
 * `pendingConsent` and dropping events unexpectedly after opt-in.
 */
let segmentMiddlewareInstalled = false;

/**
 * Module-scoped consent state. Shared by every adapter instance and
 * read by the single installed middleware. Instance methods (`optIn`
 * /`optOut`) mirror their intent into this variable so the middleware
 * doesn't have to know which adapter set the latest value.
 */
let segmentConsent: 'in' | 'out' | null = null;

function loadSegmentSdk(
  config: SegmentAdapterConfig,
): Promise<SegmentSdkHolder> {
  if (segmentLoadPromise) return segmentLoadPromise;
  segmentLoadPromise = (async () => {
    try {
      const { AnalyticsBrowser } = await import('@segment/analytics-next');
      return { browser: AnalyticsBrowser.load({ writeKey: config.writeKey }) };
    } catch (error) {
      // Clear the cached promise so a subsequent routed call can retry
      // the load instead of awaiting an already-rejected promise.
      segmentLoadPromise = null;
      throw error;
    }
  })();
  return segmentLoadPromise;
}

/** Test-only reset hook — clears module-scoped state between tests. */
export function __resetSegmentAdapterModuleStateForTesting() {
  segmentLoadPromise = null;
  segmentMiddlewareInstalled = false;
  segmentConsent = null;
}

/**
 * Segment adapter that lazy-loads `@segment/analytics-next` on first use,
 * keeping it out of the main bundle for sessions that never need it
 * (anonymous pre-consent loads, error-boundary-only sessions).
 *
 * ## Lifecycle
 *
 * 1. **Constructed by `createSegmentAdapter`** with the writeKey. No
 *    SDK loaded yet. Listed in routing as `'segment'`. The factory
 *    returns `null` when the write key is empty, so misconfigured
 *    environments naturally no-op via the routing layer.
 *
 * 2. **First routed call** (capture/captureException/identify) triggers
 *    `lazyLoadSegment()`. The call is queued for replay once the SDK
 *    finishes loading. Subsequent pre-load calls keep queuing.
 *
 * 3. **Lifecycle calls (optIn, optOut, reset)** are NOT load-triggering.
 *    They update internal pending state so the SDK applies the right
 *    consent once it does load. This means a session that only ever
 *    receives lifecycle calls never causes the Segment SDK to be fetched.
 *
 * 4. **Once loaded** — the consent middleware is installed once
 *    (module-scoped guard), the queue is drained, and all subsequent
 *    calls pass through directly.
 *
 * ## Consent
 *
 * The Segment SDK has no first-class runtime "stop sending" toggle —
 * `disable: true` exists but is load-time-only, and `analytics.reset()`
 * only clears identity. The idiomatic dynamic-consent pattern is a
 * source middleware (installed once at SDK load) that drops events when
 * the user has opted out:
 *
 *   sdk.addSourceMiddleware(({ payload, next }) => {
 *     if (segmentConsent === 'out') return; // drop, don't call next
 *     next(payload);
 *   });
 *
 * Same user-observable behaviour as a per-method gate, but events flow
 * through the SDK's normal pipeline (queueing, identity stamping) and
 * get dropped at a single, idiomatic point. It's also what Segment's
 * own `@segment/analytics-consent-tools` package does internally for
 * its opt-out mode.
 *
 * Consent state lives at module scope (`segmentConsent`) so the single
 * middleware closure observes flips made by any adapter instance.
 *
 * The middleware drops **every** event type post-optOut, including
 * `identify`. Once a user has explicitly opted out we send nothing
 * about them to Segment — no track, no page, no identify trait
 * mirroring. Storing identity information for an opted-out user is the
 * exact GDPR footgun the gate exists to prevent. The AnalyticsProvider's
 * consent effect already avoids calling `identify` post-optOut in the
 * normal flow, so this is mostly defense-in-depth for future callers
 * that bypass the provider.
 *
 * If we ever need category-based consent (Marketing/Analytics/etc.)
 * or CMP integration (OneTrust, TrustArc), upgrade to
 * `@segment/analytics-consent-tools` — it provides `createWrapper` with
 * `getCategories`, `registerOnConsentChanged`, and CMP-specific
 * wrappers. ~5–10KB gzip, lazy-loadable. Overkill for binary opt-out.
 */
export class SegmentAdapter implements AnalyticsAdapter {
  readonly name = 'segment' as const;

  private readonly writeKey: string;

  private sdk: SegmentSdkHolder | null = null;
  private loadStarted = false;

  /** Calls deferred until the SDK loads. */
  private readonly queue: Array<(sdk: SegmentSdkHolder) => void> = [];

  constructor(config: SegmentAdapterConfig) {
    this.writeKey = config.writeKey;
  }

  // ── Routed methods: trigger lazy load on first call ─────────────────

  /**
   * Segment's first-class page primitive. Pageviews land in the
   * destination's `pages` table (not `tracks`), which is what warehouse
   * consumers and Segment's Page reports expect.
   */
  page(
    name: string,
    properties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    const stripped = stripPiiKeys(properties);
    const apply = (sdk: SegmentSdkHolder) => {
      void sdk.browser.page(name, stripped);
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadSegment();
  }

  track(
    event: string,
    properties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    // Note: Segment's `track(...)` doesn't expose a sendBeacon transport
    // override, so `options.immediate` can't be honoured here. Segment's
    // batching is short-window enough that page-unload events generally
    // still get flushed, but events that absolutely must survive unload
    // should rely on the PostHog/GA branches.
    const stripped = stripPiiKeys(properties);
    const apply = (sdk: SegmentSdkHolder) => {
      void sdk.browser.track(event, stripped);
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadSegment();
  }

  trackException(
    error: unknown,
    additionalProperties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    // Segment has no first-class exception event; emit it as a track
    // event so it still lands in the warehouse for cross-source joins.
    const description = error instanceof Error ? error.message : String(error);
    const stripped = stripPiiKeys(additionalProperties);
    const apply = (sdk: SegmentSdkHolder) => {
      void sdk.browser.track('exception', { ...stripped, description });
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadSegment();
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    const apply = (sdk: SegmentSdkHolder) => {
      void sdk.browser.identify(userId, traits);
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadSegment();
  }

  // ── Lifecycle methods: update internal state, never trigger load ────

  reset() {
    if (this.sdk) {
      void this.sdk.browser.reset();
      return;
    }
    // Pre-load reset (e.g. logout before Segment finishes importing):
    // drop any queued capture/identify so they don't replay after the
    // import resolves and silently attribute the new anonymous session
    // to the prior identified user.
    this.queue.length = 0;
  }

  /**
   * Segment gates on a single consent flag read by the middleware, so it
   * follows `analytics_storage`. The ad signals are meaningless here.
   */
  setConsent(signals: ConsentSignals) {
    if (signals.analytics_storage === 'granted') {
      this.optIn();
    } else {
      this.optOut();
    }
  }

  optIn() {
    segmentConsent = 'in';
  }

  optOut() {
    segmentConsent = 'out';
    // Pre- or post-load consent revocation: drop the queue so events
    // captured under the prior (assumed-granted) consent don't fire
    // once the SDK finishes importing or replay later.
    this.queue.length = 0;
    if (this.sdk) void this.sdk.browser.reset();
  }

  toggleSessionRecording(_toState: 'on' | 'off') {
    // Segment has no session recording equivalent; intentional no-op.
  }

  isReady(): boolean {
    return this.sdk !== null;
  }

  // ── Internal ────────────────────────────────────────────────────────

  /**
   * Triggers the page-level lazy load (via `loadSegmentSdk`), then once
   * the SDK is ready installs the consent middleware (once per page)
   * and drains queued calls. Safe to call multiple times per instance —
   * only the first call kicks off the wait. The underlying
   * `AnalyticsBrowser.load` runs at most once per page regardless of
   * how many adapter instances exist.
   */
  private lazyLoadSegment(): void {
    if (this.loadStarted || globalThis.document === undefined) return;
    this.loadStarted = true;

    void (async () => {
      try {
        const sdk = await loadSegmentSdk({ writeKey: this.writeKey });
        this.sdk = sdk;

        // Install the consent middleware exactly once per page,
        // regardless of how many adapter instances trigger this code
        // path. The closure reads the module-scoped `segmentConsent`
        // so any instance's optOut/optIn is observed.
        if (!segmentMiddlewareInstalled) {
          segmentMiddlewareInstalled = true;
          void sdk.browser.addSourceMiddleware(({ payload, next }) => {
            if (segmentConsent === 'out') return;
            next(payload);
          });
        }

        // If consent flipped to 'out' while the SDK was importing,
        // skip the drain — the queue was cleared by optOut, so the
        // loop will be a no-op, but be explicit so a future caller
        // that decides to retain the queue doesn't accidentally leak.
        if (segmentConsent === 'out') {
          this.queue.length = 0;
          return;
        }

        // Drain queued calls in order. Per-call try/catch so a single
        // failing entry doesn't poison the rest of the replay.
        for (const apply of this.queue) {
          try {
            apply(sdk);
          } catch (error) {
            console.error('Segment queue drain error', error);
          }
        }
        this.queue.length = 0;
      } catch (error) {
        console.error('Failed to lazy-load Segment', error);
        // Allow a future routed call to retry; clear the queue so we
        // don't accumulate stale events across attempts.
        this.queue.length = 0;
        this.loadStarted = false;
      }
    })();
  }
}

/**
 * Factory: returns a `SegmentAdapter` when env has the write key, else
 * `null` so the provider can filter it out without knowing what counts
 * as "configured" for Segment.
 */
export function createSegmentAdapter(env: {
  SEGMENT_WRITE_KEY: string;
}): AnalyticsAdapter | null {
  if (!env.SEGMENT_WRITE_KEY) return null;
  return new SegmentAdapter({ writeKey: env.SEGMENT_WRITE_KEY });
}
